import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, Check, FileSpreadsheet, Mail, Send, X } from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { fmtDate, fmtGNF, TRANSFER_STATUS_STYLES } from "@/lib/format";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { product_id: "", quantity: 1, from_location: "", to_location: "", reason: "" };

export function AdminTransfers() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [data, setData] = useState({ items: [], pending: 0 });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([api.get("/admin/products"), api.get("/warehouse/transfers")]);
      setProducts(p.data);
      setData(t.data);
      setForm((f) => (f.product_id ? f : { ...f, product_id: p.data[0]?.id || "", from_location: p.data[0]?.location || "" }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    setForm({ ...form, product_id: id, from_location: p?.location || "" });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/warehouse/transfers", { ...form, quantity: Number(form.quantity) });
      toast.success("Transfert créé : en attente de validation par un second utilisateur");
      setForm({ ...EMPTY, product_id: form.product_id });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (t, action) => {
    try {
      await api.post(`/warehouse/transfers/${t.id}/${action}`);
      toast.success(action === "validate" ? "Transfert validé et emplacement mis à jour" : "Transfert refusé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-transfers-page">
      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Panel title="Nouveau transfert" subtitle="Déplacer des articles d'un emplacement à un autre" testid="transfer-form-panel">
          <div className="space-y-3">
            <div>
              <ALabel>Article *</ALabel>
              <ASelect value={form.product_id} onChange={(e) => pickProduct(e.target.value)} data-testid="transfer-product-select">
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.sku} — {p.name} (stock {p.stock})
                  </option>
                ))}
              </ASelect>
            </div>
            <div>
              <ALabel>Quantité *</ALabel>
              <AInput type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="transfer-quantity-input" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <ALabel>Emplacement source *</ALabel>
                <AInput value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} placeholder="A1-R2-E03" data-testid="transfer-from-input" />
              </div>
              <div>
                <ALabel>Emplacement destination *</ALabel>
                <AInput value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} placeholder="B4-R1-E11" data-testid="transfer-to-input" />
              </div>
            </div>
            <div>
              <ALabel>Motif</ALabel>
              <AInput value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Réorganisation, zone de picking…" data-testid="transfer-reason-input" />
            </div>
            <ABtn
              onClick={submit}
              disabled={busy || !form.product_id || !form.from_location || !form.to_location}
              className="w-full"
              data-testid="transfer-submit-button"
            >
              <ArrowLeftRight className="h-4 w-4" /> Demander le transfert
            </ABtn>
            <p className="rounded-xl border border-amber-900/60 bg-amber-950/30 p-3 text-xs text-amber-200" data-testid="transfer-note">
              Chaque transfert doit être validé par un second utilisateur (autre que {user?.email}) avant que
              l'emplacement de l'article soit modifié. Toute l'opération reste tracée dans l'historique des mouvements.
            </p>
          </div>
        </Panel>

        <Panel title="Transferts" subtitle={`${data.pending} en attente de validation`} testid="transfer-list-panel">
          <div className="space-y-2">
            {data.items.length === 0 && <p className="text-sm text-slate-500">Aucun transfert enregistré.</p>}
            {data.items.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-800 p-3" data-testid={`transfer-row-${t.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white">{t.product_name}</span>
                  <span className={`ds-chip ${TRANSFER_STATUS_STYLES[t.status] || ""}`} data-testid={`transfer-status-${t.id}`}>
                    {t.status}
                  </span>
                </div>
                <p className="font-num mt-1 text-xs text-slate-300">
                  {t.sku} · × {t.quantity} · {t.from_location} → {t.to_location}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Demandé par {t.created_by} · {fmtDate(t.created_at)}
                  {t.validated_by ? ` · traité par ${t.validated_by}` : ""}
                  {t.reason ? ` · ${t.reason}` : ""}
                </p>
                {t.status === "en attente" && (
                  <div className="mt-2 flex gap-2">
                    <ABtn onClick={() => decide(t, "validate")} data-testid={`transfer-validate-${t.id}`}>
                      <Check className="h-3.5 w-3.5" /> Valider
                    </ABtn>
                    <ABtn variant="danger" onClick={() => decide(t, "reject")} data-testid={`transfer-reject-${t.id}`}>
                      <X className="h-3.5 w-3.5" /> Refuser
                    </ABtn>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

const thisMonthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    });
  }
  return out;
};

export function AdminReports() {
  const months = thisMonthOptions();
  const [month, setMonth] = useState(months[0].value);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ accountant_email: "", accountant_name: "", enabled: false });
  const [busy, setBusy] = useState("");

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/admin/report-settings");
      setSettings(data);
      setDraft({ accountant_email: data.accountant_email, accountant_name: data.accountant_name, enabled: data.enabled });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/admin/reports/monthly", { params: { month } });
      setSummary(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const save = async () => {
    try {
      await api.put("/admin/report-settings", draft);
      toast.success("Paramètres du rapport mensuel enregistrés");
      loadSettings();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/admin/reports/monthly/send", null, { params: { month } });
      toast.success(`Rapport de ${data.run.month_label} envoyé à ${data.run.recipient}`);
      loadSettings();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const download = () => {
    window.open(`${API}/admin/reports/monthly/excel?month=${month}`, "_blank");
  };

  const t = summary?.totals;

  return (
    <div className="space-y-4" data-testid="admin-reports-page">
      <Panel
        title="Rapport mensuel"
        subtitle="Récapitulatif des ventes du mois pour votre comptable"
        actions={
          <>
            <ASelect value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="report-month-select">
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </ASelect>
            <ABtn variant="ghost" onClick={download} data-testid="report-download-button">
              <FileSpreadsheet className="h-3.5 w-3.5" /> Télécharger .xlsx
            </ABtn>
            <ABtn onClick={sendNow} disabled={busy === "send"} data-testid="report-send-button">
              <Send className="h-3.5 w-3.5" /> {busy === "send" ? "Envoi…" : "Envoyer au comptable"}
            </ABtn>
          </>
        }
        testid="report-summary-panel"
      >
        {!summary ? (
          <p className="text-sm text-slate-400">Chargement…</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Chiffre d'affaires", fmtGNF(t.chiffre_affaires)],
                ["Encaissé (livrées)", fmtGNF(t.encaisse)],
                ["Commandes validées", t.commandes],
                ["Commissions vendeurs", fmtGNF(t.commissions)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-800 bg-slate-950 p-3" data-testid={`report-kpi-${label}`}>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
                  <p className="font-num mt-1 text-lg font-bold text-white">{value}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-slate-400">
                  <tr className="border-b border-slate-800">
                    <th className="py-2">Top produits — {summary.label}</th>
                    <th className="py-2 text-right">Quantité</th>
                    <th className="py-2 text-right">Chiffre d'affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.top.length === 0 && (
                    <tr><td colSpan={3} className="py-3 text-sm text-slate-500">Aucune vente sur ce mois.</td></tr>
                  )}
                  {summary.top.map((p) => (
                    <tr key={p.product_name} className="border-b border-slate-800/60" data-testid={`report-top-${p.product_name}`}>
                      <td className="py-2 text-white">{p.product_name}</td>
                      <td className="font-num py-2 text-right text-slate-300">{p.qty}</td>
                      <td className="font-num py-2 text-right text-emerald-400">{fmtGNF(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      <Panel
        title="Envoi automatique au comptable"
        subtitle="Chaque 1er du mois à 07h00, le récapitulatif du mois écoulé est envoyé par e-mail"
        actions={<ABtn onClick={save} data-testid="report-settings-save">Enregistrer</ABtn>}
        testid="report-settings-panel"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <ALabel>E-mail du comptable *</ALabel>
            <AInput
              value={draft.accountant_email}
              onChange={(e) => setDraft({ ...draft, accountant_email: e.target.value })}
              placeholder="comptable@exemple.gn"
              data-testid="report-accountant-email"
            />
          </div>
          <div>
            <ALabel>Nom du comptable</ALabel>
            <AInput
              value={draft.accountant_name}
              onChange={(e) => setDraft({ ...draft, accountant_name: e.target.value })}
              placeholder="M. Camara"
              data-testid="report-accountant-name"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              data-testid="report-auto-enabled"
            />
            Activer l'envoi automatique le 1er de chaque mois
          </label>
        </div>
        {settings?.last_run && (
          <p className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-900/60 bg-emerald-950/30 p-3 text-xs text-emerald-200" data-testid="report-last-run">
            <Mail className="h-3.5 w-3.5" />
            Dernier envoi : rapport de {settings.last_run.month_label} à {settings.last_run.recipient} le{" "}
            {fmtDate(settings.last_run.created_at)} ({settings.last_run.trigger}).
          </p>
        )}
        <p className="mt-3 text-xs text-slate-400">
          L'e-mail contient le récapitulatif chiffré du mois (chiffre d'affaires, commandes, commissions, top produits).
          Le fichier Excel détaillé reste téléchargeable ci-dessus.
        </p>
      </Panel>
    </div>
  );
}
